import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAccess } from "../context/AccessContext";
import {
  fetchUserAppState,
  saveUserAppStateRoute,
} from "./services/userAppStateApi";
import {
  buildPersistableRoute,
  deriveModuleFromPath,
  isMeaningfulRoute,
  resolveRestorableRoute,
} from "../utils/userAppState";
import { logger } from "../utils/logger";

const ROUTE_SAVE_DEBOUNCE_MS = 750;

/**
 * Load the authenticated user's server-side app state and resolve a safe
 * navigation target (RBAC-validated). Returns null on failure so callers
 * can fall back to getDefaultRoute without blocking login.
 */
export async function loadRestorableUserRoute(access) {
  try {
    const state = await fetchUserAppState();
    const saved = state?.lastVisitedRoute;
    if (!saved) {
      return null;
    }
    return resolveRestorableRoute(saved, access);
  } catch (error) {
    // Persistence is optional — never block auth / navigation.
    logger.warn(
      "[userAppState] failed to load user state:",
      error?.response?.status || error?.message || error
    )
    return null;
  }
}

/**
 * Globally tracks meaningful route changes and persists them server-side.
 * Mount once inside the router (e.g. AppRoutes). Renders nothing.
 */
export function UserAppStateTracker() {
  const location = useLocation();
  const { isAuthenticated, isAccessLoading } = useAccess();
  const lastSavedRef = useRef("");
  const timerRef = useRef(null);

  const persistRoute = useCallback((pathname, search) => {
    if (!isMeaningfulRoute(pathname)) {
      return;
    }

    const route = buildPersistableRoute(pathname, search);
    if (!route || route === lastSavedRef.current) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      const module = deriveModuleFromPath(pathname);
      saveUserAppStateRoute({ route, module })
        .then(() => {
          lastSavedRef.current = route;
        })
        .catch((error) => {
          // Do not toast or disrupt navigation — persistence is best-effort.
          logger.warn(
            "[userAppState] failed to save route:",
            error?.response?.status || error?.message || error
          )
        });
    }, ROUTE_SAVE_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isAccessLoading) {
      return undefined;
    }

    persistRoute(location.pathname, location.search);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [
    isAuthenticated,
    isAccessLoading,
    location.pathname,
    location.search,
    persistRoute,
  ]);

  return null;
}

export default UserAppStateTracker;
